import { useEffect, useState, useMemo } from "react";
import {
  MapPin,
  Briefcase,
  Clock,
  DollarSign,
  ArrowRight,
  Building2,
  SearchX,
  Loader2,
} from "lucide-react";
import { axios } from "@/client/libs/axios";
import { formatJobType } from "@/client/utils/jobFacts";

// ── Types ──────────────────────────────────────────────────────────────────

type SalaryRange = {
  min: string;
  max: string;
  currency: string;
  paymentType: string;
};

type Experience = {
  min: number;
  max: number;
};

type Company = {
  id: string;
  name: string;
  logoUrl?: string | null;
};

type Job = {
  id: string;
  title: string;
  companyName: string;
  description: string;
  location: string | null;
  placement: string | null;
  type: string | null;
  experience: Experience | null;
  salaryRange: SalaryRange | null;
  salaryVisible: boolean;
  industry: string | null;
  createdAt: string;
  publishedAt: string | null;
  company: Company | null;
};

type PlacementFilter = "all" | "remote" | "hybrid" | "onsite";

// ── Helpers ────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const d = Math.floor(diffMs / 86_400_000);
  if (d === 0) return "Today";
  if (d === 1) return "Yesterday";
  if (d < 7) return `${d} days ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

function formatSalary(range: SalaryRange): string {
  const sym =
    range.currency === "USD" ? "$" : range.currency === "AUD" ? "A$" : range.currency + " ";
  const period = range.paymentType === "hourly" ? "/hr" : "/yr";
  const fmt = (v: string) => {
    const n = parseFloat(v);
    return n >= 1000 ? `${sym}${(n / 1000).toFixed(0)}K` : `${sym}${n}`;
  };
  return `${fmt(range.min)} – ${fmt(range.max)}${period}`;
}

function formatExperience(exp: Experience): string {
  if (exp.min === 0 && exp.max === 0) return "Any experience";
  if (exp.min === exp.max) return `${exp.min}+ yrs`;
  return `${exp.min}–${exp.max} yrs`;
}

function normalizePlacement(p: string | null): string {
  if (!p) return "";
  return p.toLowerCase().replace(/[-\s]/g, "");
}

// ── Sub-components ─────────────────────────────────────────────────────────

function CompanyAvatar({ name, logoUrl }: { name: string; logoUrl?: string | null }) {
  const palettes = [
    "bg-sky-500",
    "bg-emerald-500",
    "bg-violet-500",
    "bg-amber-500",
    "bg-rose-500",
    "bg-teal-500",
  ];
  const bg = palettes[name.charCodeAt(0) % palettes.length];

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name}
        className="h-11 w-11 shrink-0 rounded-xl object-contain bg-white border border-zinc-100"
      />
    );
  }

  return (
    <div
      className={`h-11 w-11 shrink-0 rounded-xl ${bg} flex items-center justify-center text-white font-bold text-lg select-none`}
      aria-hidden="true"
    >
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

const placementConfig: Record<string, { label: string; className: string }> = {
  remote:  { label: "Remote",  className: "bg-emerald-100 text-emerald-800" },
  hybrid:  { label: "Hybrid",  className: "bg-sky-100    text-sky-800"     },
  onsite:  { label: "On-site", className: "bg-amber-100  text-amber-800"   },
};

function PlacementBadge({ placement }: { placement: string | null }) {
  if (!placement) return null;
  const key = normalizePlacement(placement);
  const cfg = placementConfig[key];
  if (!cfg) return null;

  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

function JobCard({ job }: { job: Job }) {
  const postedDate = job.publishedAt ?? job.createdAt;
  const showSalary = job.salaryVisible && job.salaryRange !== null;
  // Same label source as the job share page — this used to render theRecruit's current
  // spellings raw, so a FULL_TIME role was advertised on the careers page as "FULL_TIME".
  const typeLabel = formatJobType(job.type);

  return (
    <article className="group flex flex-col bg-white rounded-2xl border border-zinc-200/80 p-6 transition-all duration-300 hover:border-zinc-300 hover:shadow-[0_8px_32px_rgba(15,23,42,0.1)]">
      {/* Header */}
      <div className="flex items-start gap-4 mb-4">
        <CompanyAvatar
          name={job.company?.name ?? job.companyName}
          logoUrl={job.company?.logoUrl}
        />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-zinc-500 truncate mb-0.5">
            {job.company?.name ?? job.companyName}
          </p>
          <h2 className="text-base font-semibold text-zinc-900 leading-snug group-hover:text-sky-700 transition-colors duration-200">
            {job.title}
          </h2>
        </div>
      </div>

      {/* Meta row */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <PlacementBadge placement={job.placement} />

        {typeLabel && (
          <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
            <Briefcase className="w-3.5 h-3.5" />
            {typeLabel}
          </span>
        )}

        {job.location && (
          <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
            <MapPin className="w-3.5 h-3.5" />
            {job.location}
          </span>
        )}
      </div>

      {/* Industry tag */}
      {job.industry && (
        <div className="mb-4">
          <span className="inline-flex items-center rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium text-zinc-600">
            {job.industry}
          </span>
        </div>
      )}

      {/* Details row */}
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-zinc-500 mb-5">
        {showSalary && (
          <span className="inline-flex items-center gap-1 font-medium text-zinc-700">
            <DollarSign className="w-3.5 h-3.5 text-emerald-500" />
            {formatSalary(job.salaryRange!)}
          </span>
        )}
        {job.experience && (
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {formatExperience(job.experience)} experience
          </span>
        )}
      </div>

      {/* Footer */}
      <div className="mt-auto flex items-center justify-between pt-4 border-t border-zinc-100">
        <span className="text-xs text-zinc-400">{timeAgo(postedDate)}</span>
        <a
          href={`/open-positions/${job.id}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-zinc-900 px-4 py-2 text-xs font-medium text-white transition-all duration-200 hover:bg-sky-600 hover:gap-2.5"
        >
          View &amp; Apply
          <ArrowRight className="w-3.5 h-3.5" />
        </a>
      </div>
    </article>
  );
}

function SkeletonCard() {
  return (
    <div className="flex flex-col bg-white rounded-2xl border border-zinc-200/80 p-6 animate-pulse">
      <div className="flex items-start gap-4 mb-4">
        <div className="h-11 w-11 shrink-0 rounded-xl bg-zinc-200" />
        <div className="flex-1 space-y-2 pt-1">
          <div className="h-3 w-24 bg-zinc-200 rounded" />
          <div className="h-4 w-3/4 bg-zinc-200 rounded" />
        </div>
      </div>
      <div className="flex gap-2 mb-4">
        <div className="h-5 w-16 bg-zinc-200 rounded-full" />
        <div className="h-5 w-20 bg-zinc-200 rounded-full" />
      </div>
      <div className="h-3 w-32 bg-zinc-200 rounded mb-5" />
      <div className="mt-auto flex items-center justify-between pt-4 border-t border-zinc-100">
        <div className="h-3 w-16 bg-zinc-200 rounded" />
        <div className="h-7 w-28 bg-zinc-200 rounded-full" />
      </div>
    </div>
  );
}

function EmptyState({ filter }: { filter: PlacementFilter }) {
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-24 text-center">
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-zinc-100">
        <SearchX className="h-8 w-8 text-zinc-400" />
      </div>
      <h3 className="mb-2 text-lg font-semibold text-zinc-900">
        {filter === "all" ? "No open positions right now" : `No ${filter} roles at the moment`}
      </h3>
      <p className="max-w-sm text-sm text-zinc-500">
        {filter === "all"
          ? "Check back soon. New roles are posted regularly."
          : "Try removing the filter to see all open positions."}
      </p>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export const CareersPage = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<PlacementFilter>("all");

  useEffect(() => {
    const load = async () => {
      try {
        // Backend caps at 20; passing 50 ensures we always get the maximum available.
        const res = await axios.get<{ data: Job[] } | Job[]>("/job-posting?limit=50");
        const raw = Array.isArray(res.data) ? res.data : (res.data as any)?.data ?? [];
        setJobs(raw);
      } catch {
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    if (activeFilter === "all") return jobs;
    return jobs.filter((j) => normalizePlacement(j.placement) === activeFilter);
  }, [jobs, activeFilter]);

  const filters: { key: PlacementFilter; label: string }[] = [
    { key: "all",    label: "All roles"    },
    { key: "remote", label: "Remote"       },
    { key: "hybrid", label: "Hybrid"       },
    { key: "onsite", label: "On-site"      },
  ];

  return (
    <main>
      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="relative bg-gradient-to-b from-[#04031a] via-[#06051e] to-[#0a0820] pt-32 pb-20 overflow-hidden">
        {/* Ambient glows */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-1/4 top-0 h-80 w-96 rounded-full bg-sky-500/5 blur-[120px]" />
          <div className="absolute right-1/4 top-16 h-72 w-80 rounded-full bg-violet-500/5 blur-[100px]" />
        </div>

        <div className="relative mx-auto max-w-4xl px-6 text-center">
          {/* Live count badge */}
          {!loading && jobs.length > 0 && (
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-sky-300">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {jobs.length} open {jobs.length === 1 ? "position" : "positions"}
            </div>
          )}

          <h1 className="font-noto-serif text-5xl font-semibold tracking-tight text-white md:text-6xl">
            Find your next{" "}
            <span className="text-sky-400">opportunity</span>
          </h1>

          <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-zinc-400">
            Browse open roles from companies hiring through theResume.
            Apply with your resume, spotlight intro, and interview performance, all in one place.
          </p>
        </div>
      </section>

      {/* ── Filter bar + job grid ────────────────────────────────────────── */}
      <section className="min-h-[60vh] bg-[#0d0b1e] px-6 py-12">
        <div className="mx-auto max-w-6xl">
          {/* Filters */}
          <div className="mb-10 flex flex-wrap items-center gap-2">
            {filters.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setActiveFilter(f.key)}
                className={[
                  "rounded-full px-5 py-2 text-sm font-medium transition-all duration-200",
                  activeFilter === f.key
                    ? "bg-sky-500 text-white shadow-[0_0_20px_rgba(14,165,233,0.3)]"
                    : "border border-white/10 bg-white/5 text-zinc-400 hover:border-white/20 hover:bg-white/10 hover:text-white",
                ].join(" ")}
              >
                {f.label}
              </button>
            ))}

            {!loading && (
              <span className="ml-auto text-sm text-zinc-500">
                {filtered.length} {filtered.length === 1 ? "role" : "roles"}
              </span>
            )}
          </div>

          {/* Grid */}
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)
            ) : filtered.length === 0 ? (
              <EmptyState filter={activeFilter} />
            ) : (
              filtered.map((job) => <JobCard key={job.id} job={job} />)
            )}
          </div>

          {/* Bottom CTA */}
          {!loading && jobs.length > 0 && (
            <div className="mt-16 rounded-2xl border border-white/10 bg-white/5 p-10 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-sky-500/10">
                <Building2 className="h-6 w-6 text-sky-400" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-white">
                Are you hiring?
              </h3>
              <p className="mb-6 text-sm text-zinc-400">
                Post a role and let our AI match you with the right candidates automatically.
              </p>
              <a
                href="https://therecruit.io"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-sky-500 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-sky-400"
              >
                Post a role on theRecruit
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};
